import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import DashboardShell, { StatCard } from "./DashboardShell";
import { Button } from "../../components/ui/button";
import { Plus, Calendar, MapPin, Users, Trash2, Sparkles, Eye, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import BoostModal from "../../components/BoostModal";
import Badges from "../../components/Badges";
import PromoCodesManager from "../../components/PromoCodesManager";

export default function OrganizerDashboard() {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState([]);
  const [boostEvent, setBoostEvent] = useState(null);

  const load = async () => {
    const [e, b, s] = await Promise.all([
      api.get("/my/events"),
      api.get("/seller/bookings"),
      api.get("/my/events/stats"),
    ]);
    setEvents(e.data);
    setBookings(b.data);
    setStats(s.data);
  };

  useEffect(() => { load(); }, []);

  const removeEvent = async (id) => {
    if (!confirm("Supprimer cet événement ?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Supprimé");
    load();
  };

  const revenue = bookings.filter(b => b.payment_status === "paid").reduce((s, b) => s + (b.seller_amount || 0), 0);
  const totalViews = stats.reduce((s, x) => s + (x.views_total || 0), 0);
  const statsById = Object.fromEntries(stats.map((s) => [s.event_id, s]));

  return (
    <DashboardShell
      title="Mes événements"
      subtitle="Créez et gérez vos événements"
      actions={
        <Link to="/dashboard/events/new">
          <Button data-testid="new-event-cta" className="bg-ink hover:bg-ink/85 text-cream rounded-none">
            <Plus className="h-4 w-4 mr-2" /> Nouvel événement
          </Button>
        </Link>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <StatCard label="Événements" value={events.length} />
        <StatCard label="Vues totales" value={totalViews} />
        <StatCard label="Réservations" value={bookings.filter(b => b.payment_status === "paid").length} />
        <StatCard label="Stands vendus" value={bookings.filter(b => b.item_type === "booth" && b.payment_status === "paid").length} />
        <StatCard label="Revenus" value={`${revenue.toFixed(0)}€`} accent />
      </div>

      <div className="bg-white border border-ink/10 rounded-md">
        <div className="p-5 border-b border-ink/10">
          <h2 className="font-heading font-medium text-lg text-ink">Mes événements</h2>
        </div>
        {events.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-muted-foreground mb-4">Aucun événement pour l'instant.</p>
            <Link to="/dashboard/events/new">
              <Button className="bg-ink hover:bg-ink/85 text-cream rounded-none">Créer mon premier événement</Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-ink/10">
            {events.map((e) => {
              const st = statsById[e.id] || {};
              return (
              <div key={e.id} className="p-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <img src={e.images?.[0] || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=200"} alt="" className="h-16 w-16 object-cover flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/events/${e.id}`} className="font-heading font-medium text-ink hover:text-accent truncate">{e.title}</Link>
                      {e.is_boosted && (
                        <span className="inline-flex items-center gap-1 bg-accent text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5">
                          <Sparkles className="h-3 w-3" /> À la une
                        </span>
                      )}
                    </div>
                    {st.badges?.length > 0 && (
                      <div className="mt-2">
                        <Badges badges={st.badges} variant="card" />
                      </div>
                    )}
                    <div className="flex items-center gap-x-3 gap-y-1 text-xs text-ink/55 mt-1 flex-wrap">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.city}</span>
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(e.start_date).toLocaleDateString("fr-FR")}</span>
                      <span className="flex items-center gap-1" data-testid={`views-${e.id}`}>
                        <Eye className="h-3 w-3" /> <strong className="text-ink">{st.views_total || 0}</strong> vues
                        {st.views_7d > 0 && <span className="text-ink/40">· {st.views_7d} sur 7j</span>}
                      </span>
                      {st.is_boosted && st.views_during_boost > 0 && (
                        <span className="inline-flex items-center gap-1 text-accent">
                          <TrendingUp className="h-3 w-3" /> {st.views_during_boost} vues depuis le boost
                        </span>
                      )}
                      {e.region && <span className="text-ink/40">· {e.region}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!e.is_boosted && (
                    <Button
                      data-testid={`boost-event-${e.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => setBoostEvent(e)}
                      className="border-accent text-accent hover:bg-accent hover:text-white"
                    >
                      <Sparkles className="h-3 w-3 mr-1" /> Booster
                    </Button>
                  )}
                  <Link to={`/events/${e.id}`}><Button variant="outline" size="sm">Voir</Button></Link>
                  <Button data-testid={`delete-event-${e.id}`} variant="ghost" size="sm" onClick={() => removeEvent(e.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-ink/10 rounded-md mt-8">
        <div className="p-5 border-b border-ink/10">
          <h2 className="font-heading font-medium text-lg text-primary flex items-center gap-2">
            <Users className="h-4 w-4" /> Réservations reçues
          </h2>
        </div>
        {bookings.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground">Pas encore de réservations.</p>
        ) : (
          <div className="divide-y divide-ink/10">
            {bookings.map((b) => (
              <div key={b.id} className="p-5 flex justify-between items-center">
                <div>
                  <p className="font-semibold">{b.item_title}</p>
                  <p className="text-xs text-muted-foreground">Par {b.buyer?.name} · {b.buyer?.email}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading font-black text-ink">{b.seller_amount?.toFixed(2)}€</p>
                  <p className={`text-xs ${b.payment_status === "paid" ? "text-trust" : "text-accent"}`}>
                    {b.payment_status === "paid" ? "Payé" : "En attente"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PromoCodesManager />

      <BoostModal
        event={boostEvent}
        open={!!boostEvent}
        onOpenChange={(o) => !o && setBoostEvent(null)}
      />
    </DashboardShell>
  );
}
